import type { ListingType, PriceType } from '@/types/marketplace'

const CURRENCY_LABELS: Record<string, string> = {
    EUR: 'EUR',
    ALL: 'Lek',
    USD: 'USD'
}

const LISTING_TYPE_LABELS: Record<ListingType, string> = {
    selling: 'For sale',
    buying: 'Wanted',
    service: 'Service',
    job: 'Job'
}

export const listingTypeLabel = (type: ListingType): string => LISTING_TYPE_LABELS[type] ?? 'Listing'

export const currencyLabel = (code: string): string => CURRENCY_LABELS[code] ?? code

/**
 * Renders the money line for a card or detail page. Never throws on a
 * missing price, because `contact` and `free` listings legitimately have none.
 */
export const formatPrice = (price: number | null, currency: string, priceType: PriceType): string => {
    if (priceType === 'free') return 'Free'
    if (priceType === 'contact') return 'Contact for price'

    if (price === null || Number.isNaN(price)) return 'Contact for price'

    const rounded = Number(price)
    const hasCents = Math.abs(rounded % 1) > 0.001

    const amount = rounded.toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0
    })

    const label = currencyLabel(currency)
    const money = currency === 'ALL' ? `${amount} ${label}` : `${label} ${amount}`

    return priceType === 'negotiable' ? `${money} - negotiable` : money
}

/**
 * Compact relative age. Always call this on the server and pass the result
 * down as a string, so the browser never renders a different clock value.
 */
export const relativeTime = (value: string | Date | null, now: Date = new Date()): string => {
    if (!value) return 'Not published'

    const then = value instanceof Date ? value : new Date(value)
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

    if (Number.isNaN(seconds)) return ''
    if (seconds < 60) return 'Just now'

    const minutes = Math.floor(seconds / 60)

    if (minutes < 60) return `${minutes}m ago`

    const hours = Math.floor(minutes / 60)

    if (hours < 24) return `${hours}h ago`

    const days = Math.floor(hours / 24)

    if (days < 7) return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    if (days < 365) return `${Math.floor(days / 30)}mo ago`

    return `${Math.floor(days / 365)}y ago`
}

export const formatDate = (value: string | Date | null): string => {
    if (!value) return 'Unknown'

    const date = value instanceof Date ? value : new Date(value)

    if (Number.isNaN(date.getTime())) return 'Unknown'

    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const formatMonthYear = (value: string | Date | null): string => {
    if (!value) return 'Unknown'

    const date = value instanceof Date ? value : new Date(value)

    if (Number.isNaN(date.getTime())) return 'Unknown'

    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

/** URL and Telegram safe slug. Handles Albanian diacritics. */
export const slugify = (input: string): string => {
    const map: Record<string, string> = {
        ë: 'e', Ë: 'e', ç: 'c', Ç: 'c', ü: 'u', ö: 'o', ä: 'a', ñ: 'n',
        á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', â: 'a', ê: 'e', î: 'i', ô: 'o', û: 'u'
    }

    return input
        .split('')
        .map((char) => map[char] ?? char)
        .join('')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 70)
        .replace(/-+$/g, '')
}

/** Short random suffix so two identical titles never collide on slug. */
export const slugSuffix = (): string => Math.random().toString(36).slice(2, 8)

export const truncate = (input: string, max: number): string => {
    const clean = input.trim()

    if (clean.length <= max) return clean

    return `${clean.slice(0, max - 1).trimEnd()}...`
}

export const telegramLink = (username: string | null): string | null => {
    if (!username) return null

    return `https://t.me/${username.replace(/^@/, '')}`
}

export const initials = (name: string): string => name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'M'
